import { useCallback, useEffect, useRef, useState } from 'react';
import AIChatTextarea, { type PresetPrompt } from './AIChatTextarea';
import AiResult, { type SearchResultData, type SearchResultItem } from './ai-result';
import SearchAnimate from './SearchAnimate';
import { askAIEmailFinder, getAIEmailFinderResult } from '@/lib/service/dashboard';
import { convertFollowerRange } from './utils';
import AIErrorModal from './AIErrorModal';
import { useTranslation } from 'next-i18next';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

export type { PresetPrompt } from './AIChatTextarea';

// 筛选条件类型
export interface SearchFilters {
  country: string;
  followerRange: [number, number];
}

interface Props {
  prompts: PresetPrompt[];
  userCreditInfo?: {
    has_subscription?: boolean;
    expire_time?: string;
  };
  onTaskIdChange?: (taskId: number | null) => void;
  initialTaskId?: number | null;
  isLogin?: boolean;
  onOpenLoginModal?: () => void;
}

// 搜索状态
type SearchState = 'idle' | 'searching' | 'result' | 'empty';

// API 列表项类型
interface AIEmailListItem {
  pk_id?: string;
  username?: string;
  full_name?: string;
  media_url?: string;
  profile_pic_url?: string;
  following_count?: number;
  follower_count?: number;
  media_count?: number;
  profile?: string;
  category?: string;
  verified?: boolean;
  email?: string;
  match_score?: number;
  match_type?: number;
  is_selected?: boolean;
}

// API 任务响应类型
interface AITaskResponse {
  id?: number;
  uid?: string;
  content?: string;
  area?: string;
  status?: number; // 0 未开始 1 AI 分析完成 2. 获取列表完成 3.分析无结果 -1 错误
  user_intent?: string;
  target_niche?: string;
  audience_descriptors?: string;
  ai_location?: string;
  content_features?: string;
  email_advices?: string[];
  email_advice?: string;
  avg_match_score?: number;
  total_user_count?: number;
  cost_time?: number;
  data_url?: string;
  list?: AIEmailListItem[];
}

export default function AIDiscover({
  prompts,
  userCreditInfo,
  onTaskIdChange,
  initialTaskId,
  isLogin,
  onOpenLoginModal,
}: Props) {
  const { t } = useTranslation('common');

  // 搜索流程状态
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [promptStatus, setPromptStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [promptErrorMessage, setPromptErrorMessage] = useState<string>();
  const [searchStatus, setSearchStatus] = useState<'loading' | 'success' | 'empty'>('loading');
  const [searchErrorMessage, setSearchErrorMessage] = useState<string>();
  const [errorKind, setErrorKind] = useState<'promptInvalid' | 'noResults' | null>(null);

  // 错误弹窗状态
  const [errorModalData, setErrorModalData] = useState<{
    title: string;
    desc: string;
    example?: string;
    btnTitle: string;
  } | null>(null);

  // 提升的共享状态
  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({ country: '', followerRange: [0, 7] });
  const [searchResultData, setSearchResultData] = useState<SearchResultData | undefined>();

  // 轮询定时器引用
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const taskIdRef = useRef<number | null>(null);

  // 清除轮询定时器
  const clearPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  // 将 API 列表项转换为组件所需格式
  const convertToSearchResultItem = useCallback((item: AIEmailListItem, index: number): SearchResultItem => {
    // 获取匹配分数和描述
    const matchType = item.match_type || 1;

    // 处理 category 字段：可能是字符串、数组或逗号分隔的字符串
    const tags: string[] = [];
    if (item.category) {
      tags.push(item.category);
    }

    return {
      id: item.pk_id ? parseInt(item.pk_id) : index,
      avatar: item.profile_pic_url || '',
      username: item.username || '',
      fullName: item.full_name || '',
      email: item.email,
      verified: item.verified, // API 没有提供此字段，默认为 false
      description: item.profile || '',
      tags,
      followerCount: item.follower_count || 0,
      followingCount: item.following_count || 0,
      matchType,
    };
  }, []);

  // 轮询任务结果
  const pollTaskResult = useCallback(
    async (taskId: number) => {
      try {
        const response = await getAIEmailFinderResult(taskId);

        if (response.code !== 0 || !response.data?.task) {
          // API 返回错误（如 code=10001），显示无结果错误
          console.error('Polling failed:', response);
          clearPolling();
          setPromptStatus('success'); // 跳过动画
          setSearchStatus('empty');
          setSearchErrorMessage(t('No matching profiles found. Try different keywords.'));
          return;
        }

        const taskData: AITaskResponse = response.data.task;
        const listData = response.data.list; // list 在 response.data.list 里

        // 检查任务状态
        // status=0: 未开始，保持第一步
        // status=1: AI 分析完成，动画进入第二步
        // status=3: 第一步报错
        // status=2: 看 list 是否有值，有值成功，无值第三步报错

        if (taskData.status === 0) {
          // 未开始，保持第一步动画
          // 不做任何处理，继续轮询
          return;
        }

        if (taskData.status === 1) {
          // AI 分析完成，动画进入第二步
          setPromptStatus('success');
          return;
        }

        if (taskData.status === 3) {
          // 第一步报错
          clearPolling();
          setPromptStatus('error');
          setPromptErrorMessage(t('Invalid input. Please check your search criteria.'));
          setErrorKind('promptInvalid');
          return;
        }

        if (taskData.status === 2) {
          // 检查 list 是否有值
          clearPolling();
          setPromptStatus('success'); // 确保第一步完成

          if (listData && listData.length > 0) {
            // list 有值，成功
            const cards = [
              {
                id: '1',
                title: t('Target Niche'),
                subTitle: taskData.target_niche || t('Unknown'),
                iconSrc: '/images/instagram-email-finder/circle_icon.svg',
                color: 'bg-[#D1E0FF]',
              },
              {
                id: '2',
                title: t('Audience Profile'),
                subTitle: taskData.audience_descriptors || t('Unknown'),
                iconSrc: '/images/instagram-email-finder/audience.svg',
                color: 'bg-[#DBF2DD]',
              },
              {
                id: '3',
                title: t('Region'),
                subTitle: taskData.ai_location || taskData.area || t('Unknown'),
                iconSrc: '/images/instagram-email-finder/location.svg',
                color: 'bg-[#FFEDEB]',
              },
            ];

            // 构建完整的搜索结果数据
            const results = listData.map((item: AIEmailListItem, index: number) =>
              convertToSearchResultItem(item, index)
            );

            const resultData: SearchResultData = {
              intent: taskData.user_intent,
              insights: taskData.content_features,
              tips: taskData.email_advices || [],
              cards: cards as any,
              results: results,
            };

            setSearchResultData(resultData);
            setSearchStatus('success');
            setSearchState('result');
            setErrorKind(null);
          } else {
            // list 无值，第三步报错
            setSearchStatus('empty');
            setSearchErrorMessage(t('No matching profiles found. Try different keywords.'));
            setErrorKind('noResults');
          }
          return;
        }

        if (taskData.status === -1) {
          // 错误
          clearPolling();
          setPromptStatus('error');
          setPromptErrorMessage(t('An error occurred during search. Please try again.'));
          setErrorKind('promptInvalid');
          return;
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    },
    [clearPolling, convertToSearchResultItem, currentFilters.country]
  );

  // 处理搜索提交
  const handleSearch = useCallback(
    async (query: string, filters: SearchFilters) => {
      // 清除之前的轮询
      clearPolling();

      // 保存搜索参数
      setCurrentFilters(filters);

      // 重置状态
      setSearchState('searching');
      setPromptStatus('loading');
      setPromptErrorMessage(undefined);
      setSearchStatus('loading');
      setSearchErrorMessage(undefined);
      setErrorKind(null);

      try {
        // 转换粉丝范围索引为实际数值
        const [followerStart, followerEnd] = convertFollowerRange(filters.followerRange);

        ga_logEvent(EventName.ai_search_execute_click);

        // 调用 AI 搜索 API（创建任务）
        const response = await askAIEmailFinder({
          content: query,
          area: filters.country,
          follower_start_at: followerStart,
          follower_end_at: followerEnd,
        });

        // 检查响应是否成功（验证输入是否合法）
        if (response.code !== 0 || !response.data) {
          setPromptStatus('error');
          setPromptErrorMessage(response.message || t('Invalid input. Please check your search criteria.'));
          setErrorKind('promptInvalid');
          return;
        }

        const taskData: AITaskResponse = response.data;
        const taskId = taskData.id;

        if (!taskId) {
          setPromptStatus('error');
          setPromptErrorMessage(t('Failed to create search task'));
          setErrorKind('promptInvalid');
          return;
        }

        // 保存任务 ID
        taskIdRef.current = taskId;
        onTaskIdChange?.(taskId);

        // 开始轮询任务结果（每 2.5 秒轮询一次）
        pollingTimerRef.current = setInterval(() => {
          pollTaskResult(taskId);
        }, 2500);

        // 立即执行一次轮询
        pollTaskResult(taskId);
      } catch (error: any) {
        console.error('AI Search Error:', error);
        setPromptStatus('error');
        setPromptErrorMessage(error?.message || t('An unexpected error occurred'));
        setErrorKind('promptInvalid');
      }
    },
    [clearPolling, pollTaskResult]
  );

  // 处理错误弹窗
  const handleError = useCallback(
    (message: string) => {
      // 根据错误类型显示不同的弹窗内容
      if (errorKind === 'noResults') {
        // 第三步错误：无结果
        setErrorModalData({
          title: t('Oops! No results found.'),
          desc: t("We couldn't find any results for your search. Try using broader terms or a different search."),
          btnTitle: t('Try Again'),
        });
      } else {
        // 第一步错误：提示词无效
        const examplePrompt = prompts.length > 0 ? prompts[0].prompt : undefined;
        setErrorModalData({
          title: t('Heads up!'),
          desc: t(
            "It looks like we couldn't quite understand your request.\nCould you try again with a more specific description?\nHere's an example to help:"
          ),
          example: examplePrompt,
          btnTitle: t('Try Again'),
        });
      }
    },
    [errorKind, t, prompts]
  );

  // 重新搜索
  const handleRetry = useCallback(() => {
    // 清除轮询
    clearPolling();
    taskIdRef.current = null;
    onTaskIdChange?.(null);

    // 重置所有状态
    setSearchState('idle');
    setPromptStatus('loading');
    setPromptErrorMessage(undefined);
    setSearchStatus('loading');
    setSearchErrorMessage(undefined);
    setSearchResultData(undefined);
    setCurrentFilters({ country: '', followerRange: [0, 7] });
    setErrorKind(null);
  }, [clearPolling, onTaskIdChange]);

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  // 初始化：如果有 initialTaskId，恢复轮询
  useEffect(() => {
    if (initialTaskId && searchState === 'idle') {
      taskIdRef.current = initialTaskId;
      setSearchState('searching');
      setPromptStatus('loading');
      setSearchStatus('loading');

      // 开始轮询任务结果
      pollingTimerRef.current = setInterval(() => {
        pollTaskResult(initialTaskId);
      }, 2500);

      // 立即执行一次轮询
      pollTaskResult(initialTaskId);
    }
  }, [initialTaskId]); // 只在初始化时执行一次

  // 处理错误弹窗关闭
  const handleErrorModalClose = useCallback(
    (isOk: boolean) => {
      setErrorModalData(null);
      if (isOk) {
        // 点击 Try Again，重置所有状态
        handleRetry();
      }
    },
    [handleRetry]
  );

  return (
    <div>
      {searchState === 'idle' && (
        <div className="max-w-[800px] mx-auto pt-14 px-4 max-md:pt-8">
          <AIChatTextarea
            prompts={prompts}
            onSubmit={handleSearch}
            isLogin={isLogin}
            onTriggerLogin={onOpenLoginModal}
          />
        </div>
      )}

      {/* 搜索动画 */}
      {searchState === 'searching' && (
        <div className="max-w-[1000px] mx-auto pt-14 px-4 max-md:pt-8">
          <SearchAnimate
            promptStatus={promptStatus}
            promptErrorMessage={promptErrorMessage}
            searchStatus={searchStatus}
            searchErrorMessage={searchErrorMessage}
            onError={handleError}
          />
        </div>
      )}

      {/* 搜索结果 */}
      {searchState === 'result' && (
        <div className="max-w-[1000px] mx-auto">
          <AiResult data={searchResultData} onBack={handleRetry} userCreditInfo={userCreditInfo} />
        </div>
      )}

      {/* 错误弹窗 */}
      <AIErrorModal
        open={errorModalData !== null}
        title={errorModalData?.title}
        desc={errorModalData?.desc}
        example={errorModalData?.example}
        btnTitle={errorModalData?.btnTitle}
        onClose={handleErrorModalClose}
      />
    </div>
  );
}
